import resolveResponse from 'contentful-resolve-response'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ThemeProvider, Box} from '@primer/react-brand'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'

import {ZodSilentErrorBoundary} from '../../../../components/ZodSilentErrorBoundary/ZodSilentErrorBoundary'
import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'
import type {GenericGroup, GenericSectionWithIds} from '../../../../brand/lib/types/contentful'
import {cohortFunnelBuilder} from '../../../../lib/analytics'

import CallToAction from '../_components/CallToAction'
import {PricingPlans} from '../_components/PricingPlans/PricingPlans'
import SmallIdeSection from '../_components/SmallIdeSection'

import {FaqSection} from '../_sections/FaqSection/FaqSection'
import {FootnotesSection} from '../_sections/FootnotesSection/FootnotesSection'

import PricingBackground from '../_assets/pricing-bg.webp'
import PricingBackgroundSm from '../_assets/pricing-bg-sm.webp'

import {PlanTypeContextProvider} from '../_context/PlanTypeContext'

import {CompareTable} from './CompareTable'
import Hero from './Hero'

export default function FeaturesCopilotPlansIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = resolveResponse(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav} = template.fields
  const {featuresCopilotFaqSection, featuresCopilotPlansComparison} = page.ids as {
    featuresCopilotFaqSection: GenericSectionWithIds
    featuresCopilotPlansComparison: GenericSectionWithIds
  }

  const {featuresCopilotPlansFootnotes} = featuresCopilotFaqSection?.ids as {
    featuresCopilotPlansFootnotes: GenericGroup
  }

  let {copilotBusinessContactSalesPath} = useRoutePayload<{copilotBusinessContactSalesPath: string}>()
  let {copilotBusinessSignupPath} = useRoutePayload<{copilotBusinessSignupPath: string}>()
  let {copilotEnterpriseContactSalesPath} = useRoutePayload<{copilotEnterpriseContactSalesPath: string}>()
  let {copilotEnterpriseSignupPath} = useRoutePayload<{copilotEnterpriseSignupPath: string}>()
  let {copilotProPlusSignupPath} = useRoutePayload<{copilotProPlusSignupPath: string}>()
  let {copilotProSignupPath} = useRoutePayload<{copilotProSignupPath: string}>()
  const {cft} = useRoutePayload<{cft: string}>()

  const withCft = cohortFunnelBuilder(cft)

  copilotBusinessContactSalesPath = withCft(copilotBusinessContactSalesPath)
  copilotBusinessSignupPath = withCft(copilotBusinessSignupPath, {product: 'cfb'})
  copilotEnterpriseContactSalesPath = withCft(copilotEnterpriseContactSalesPath)
  copilotEnterpriseSignupPath = withCft(copilotEnterpriseSignupPath, {product: 'ce'})
  copilotProSignupPath = withCft(copilotProSignupPath, {product: 'cfi'})
  copilotProPlusSignupPath = withCft(copilotProPlusSignupPath, {product: 'cpp'})

  return (
    <ZodSilentErrorBoundary>
      <PlanTypeContextProvider>
        <ThemeProvider colorMode="dark" className="lp-Copilot position-relative">
          <Box className="lp-SubNav-spacer" />

          {subnav ? (
            <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" />
          ) : null}

          <picture>
            <source srcSet={PricingBackgroundSm} media="(max-width: 767px)" />
            <img src={PricingBackground} className="position-absolute top-0 left-0 width-full height-auto" alt="" />
          </picture>

          <Hero />

          <PricingPlans
            copilotBusinessContactSalesPath={copilotBusinessContactSalesPath}
            copilotBusinessSignupPath={copilotBusinessSignupPath}
            copilotEnterpriseContactSalesPath={copilotEnterpriseContactSalesPath}
            copilotEnterpriseSignupPath={copilotEnterpriseSignupPath}
            copilotProPlusSignupPath={copilotProPlusSignupPath}
            copilotProSignupPath={copilotProSignupPath}
          />

          <SmallIdeSection />

          {featuresCopilotPlansComparison ? <CompareTable contentfulContent={featuresCopilotPlansComparison} /> : null}

          <section className="lp-Section pt-5 pt-lg-8">
            <CallToAction copilotContactSalesPath={copilotBusinessContactSalesPath} />
          </section>

          {featuresCopilotFaqSection ? <FaqSection contentfulContent={featuresCopilotFaqSection} /> : null}

          {featuresCopilotPlansFootnotes ? (
            <FootnotesSection contentfulContent={featuresCopilotPlansFootnotes} />
          ) : null}
        </ThemeProvider>
      </PlanTypeContextProvider>
    </ZodSilentErrorBoundary>
  )
}

try{ FeaturesCopilotPlansIndex.displayName ||= 'FeaturesCopilotPlansIndex' } catch {}